import { useFragment } from "@/generated";
import { RestoreSlidesDocument, SlideFieldsFragmentDoc } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

export function useRestoreSlides(lessonId: string | null) {
  const [restoreSlidesMutation, { loading, error }] = useMutation(RestoreSlidesDocument, {
    update(cache, { data }, { variables }) {
      if (!data?.restoreSlides) return;

      const trashedSlides = data.restoreSlides;
      const trashedCount = data.restoreSlides.length;
      const trashedIds = new Set(variables?.slideIds);

      try {
        cache.batch({
          update(cache) {
            // Update slide isTrash
            for (const slide of trashedSlides) {
              const slideId = cache.identify(slide);
              if (slideId) {
                cache.modify({
                  id: slideId,
                  fields: {
                    isTrash: () => false,
                  },
                });
              }
            }

            cache.modify({
              fields: {
                slidesByLessonId(existingData, { readField, storeFieldName }) {
                  const lessonKey = `"lessonId":"${lessonId}"`;

                  if (!existingData?.items || !storeFieldName.includes(lessonKey))
                    return existingData;

                  const isActiveView = storeFieldName.includes('"isTrash":false');
                  const isTrashView = storeFieldName.includes('"isTrash":true');

                  // Remove slides from trash view (only for matching lesson)
                  if (isTrashView) {
                    const filteredItems = existingData.items.filter((slideRef: Reference) => {
                      const slideId = readField<string>("id", slideRef);
                      return slideId ? !trashedIds.has(slideId) : true;
                    });

                    return {
                      ...existingData,
                      items: filteredItems,
                      total: filteredItems.length,
                    };
                  }

                  // Add slides to active view (only for matching lesson)
                  if (isActiveView) {
                    const restoredRefs = data.restoreSlides
                      .filter((slide) => slide !== null)
                      .map((slide) => cache.identify(slide))
                      .filter((id) => id !== null)
                      .map((id) => ({ __ref: id }));

                    const updatedItems = [...restoredRefs, ...existingData.items];

                    return {
                      ...existingData,
                      items: updatedItems,
                      total: updatedItems.length,
                    };
                  }

                  return existingData;
                },
              },
            });

            // Update lesson's trashCount
            if (lessonId) {
              cache.modify({
                id: `Lesson:${lessonId}`,
                fields: {
                  trashCount(existingData = 0) {
                    return existingData - trashedCount;
                  },
                },
              });
            }
          },
        });
      } catch (error) {
        console.error("Move slides to trash cache update failed:", error);
      }
    },
  });

  const restoreSlides = async (slideIds: string[]) => {
    return withToast(
      async () => {
        const result = await restoreSlidesMutation({
          variables: { slideIds },
        });

        const restoredSlides = result.data?.restoreSlides ?? [];
        return restoredSlides.map((slide) => useFragment(SlideFieldsFragmentDoc, slide));
      },
      {
        loading: `Restoring ${slideIds.length} slide${slideIds.length !== 1 ? "s" : ""}...`,
        success: `Successfully restored ${slideIds.length} slide${slideIds.length !== 1 ? "s" : ""}`,
        error: "Failed to restore slides",
      },
      "restoreSlides"
    );
  };

  return { restoreSlides, loading, error };
}
